/**
 * Contextual Help Content Configuration
 *
 * Single source of truth for all help content displayed by the HelpButton component.
 * Each key maps to a feature/page/modal and provides the popup content.
 *
 * Structure:
 * - title: Hebrew title shown in the popup header
 * - tooltipLabel: Text for the hover tooltip
 * - content: JSX for rich structured layout
 * - image: Optional image path for visual explanation (null if none)
 */

export const HELP_CONTENT = {
  // ============================================
  // PAGE-LEVEL HELP
  // ============================================

  home_dashboard: {
    title: "דף הבית",
    tooltipLabel: "דף הבית",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          דף הבית הוא מרכז הבקרה האישי שלך — המקום שמחכה לך
          בכל כניסה למערכת. כאן אפשר לראות בדיוק מה קורה בקליניקה
          ולטפל במה שצריך, בלי לחפש.
        </p>

        <h4 className="help-rich-heading">סטטיסטיקות מהירות</h4>
        <p className="help-rich-text">
          בחלק העליון של המסך מופיעים מספרים שמסכמים את המצב שלך במבט אחד:
          פגישות היום, מטופלים פעילים, משימות פתוחות וסיכום כספי חודשי.
          ככה תמיד תדע/י איפה את/ה עומד/ת.
        </p>

        <h4 className="help-rich-heading">הסעיפים שלך</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פעולות ממתינות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פגישות שהסתיימו וממתינות לאישור, תיעוד ותשלום
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">התראות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הודעות מהמערכת: טפסים שמולאו, תזכורות ועוד
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">יומן</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הפגישות של היום והשבוע הקרוב במבט מהיר
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">משימות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              רשימת המשימות הפתוחות — אפשר לסמן &quot;בוצע&quot; ישירות מכאן
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תזכורות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תזכורות שהגדרת לעצמך, ממוינות לפי תאריך
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מתעניינים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פניות חדשות מלקוחות פוטנציאליים שממתינות לטיפול
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אפשר ליצור פגישה, הכנסה או חשבונית ישירות מדף הבית —
            פשוט לחצ/י על כפתור ה-&quot;+&quot; הצף בצד המסך.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  pending_actions: {
    title: "פעולות ממתינות",
    tooltipLabel: "פעולות ממתינות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מרוכזות כל הפגישות שהסתיימו וממתינות לטיפול שלך.
          המערכת מנחה אותך בשלושה שלבים פשוטים — אחד אחרי השני,
          בקצב שלך.
        </p>

        <h4 className="help-rich-heading">שלב 1 — אישור</h4>
        <p className="help-rich-text">
          האם הפגישה התקיימה?
        </p>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">התקיימה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">ממשיכים לשלב הבא</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">לא התקיימה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              אפשר לבטל, ואם רלוונטי — לתעד דמי ביטול
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">טרם התקיימה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הפגישה נשארת ברשימה ותחכה עד שתעדכן/י
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">שלב 2 — סיכום</h4>
        <p className="help-rich-text">
          האם לתעד את הטיפול?
        </p>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">כתוב סיכום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">עובר ישירות לדף תיעוד הטיפול</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">דלג</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              ממשיכים בלי סיכום (תמיד אפשר לחזור אחר כך)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תזכיר לי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              נוצרת משימה עם תזכורת לכתוב סיכום
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">שלב 3 — תשלום</h4>
        <p className="help-rich-text">
          האם התשלום טופל?
        </p>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שולם</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מתעדים סכום ואמצעי תשלום, עם אפשרות להפקת חשבונית
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">דלג</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              החוב יישאר פתוח בעמוד הפיננסים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תזכיר לי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              נוצרת משימה עם תזכורת לגביית התשלום
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אחרי שסיימת את כל השלבים, הפגישה נעלמת מהרשימה — נקי וברור.
          לא חייבים לטפל בהכל ברצף — אפשר לדלג ולחזור מאוחר יותר.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם מורנינג מחובר, בשלב התשלום תוכל/י להפיק חשבונית
            ישירות בלחיצה אחת.
          </span>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">הדגמה</span>
          <span>
            רוצה להתנסות בלי לגעת בנתונים אמיתיים? כפתור ההפעלה ▶ שליד סימן
            השאלה פותח פגישה לדוגמה שאפשר לעדכן מתחילתה ועד סופה.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  patients_list: {
    title: "המטופלים שלי",
    tooltipLabel: "עמוד המטופלים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          זה העמוד המרכזי שבו מנוהלים כל המטופלים שלך.
          כאן אפשר לחפש, לסנן, ולגשת לכל כרטיס מטופל בקלות.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות כאן</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">חיפוש</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לפי שם, שם משפחה או מספר טלפון
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סינון לפי סטטוס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פעיל, בהמתנה, סיים טיפול ועוד
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תצוגה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              להחליף בין טבלה לכרטיסיות — לפי מה שנוח לך
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מטופל חדש</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לחצ/י על &quot;+ מטופל חדש&quot; כדי להוסיף לרשימה
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          לחיצה על שורה או כרטיסייה של מטופל תיקח אותך ישירות לכרטיס המלא שלו —
          עם כל ההיסטוריה, הקבצים, הטפסים והתשלומים.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            המספר שמופיע ליד הכותרת מראה כמה מטופלים יש בסינון
            הנוכחי. שדה החיפוש מהיר מאוד — הוא מחפש בכל השדות בו-זמנית.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  patient_profile: {
    title: "כרטיס מטופל",
    tooltipLabel: "כרטיס המטופל",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כרטיס המטופל הוא ה&quot;תיק&quot; הדיגיטלי המלא — הכל נמצא כאן
          במקום אחד, מסודר ונגיש.
        </p>

        <h4 className="help-rich-heading">הלשוניות בכרטיס</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרופיל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פרטים אישיים, קליניים וניהוליים. לחצ/י &quot;עריכה&quot; כדי לעדכן
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">טיפולים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              היסטוריית כל הטיפולים שבוצעו, כולל סיכומים ותיעוד
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תיק מטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כל הקבצים המשויכים: מסמכים, תמונות, אישורים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">טפסים ושאלונים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              טפסים שנשלחו למטופל ושאלונים שמילא
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תשלומים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מעקב פיננסי: תשלומים, חובות פתוחים וחשבוניות
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          בתוך לשונית &quot;פרופיל&quot; יש תת-לשוניות: פרטים בסיסיים, פרטים נוספים,
          רקע רפואי וניהול. ככל שתמלא/י יותר מידע — כך סיכומי ה-AI יהיו מדויקים
          ורלוונטיים יותר.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אפשר ליצור תיעוד טיפול חדש ישירות מלשונית &quot;טיפולים&quot;
            בלחיצה על &quot;+ תיעוד חדש&quot;.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  create_patient: {
    title: "יצירת מטופל חדש",
    tooltipLabel: "יצירת מטופל",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן יוצרים מטופל חדש במערכת. התהליך פשוט ומהיר —
          ממלאים את מה שיש, ומשלימים את השאר בהמשך.
        </p>

        <h4 className="help-rich-heading">שדות חובה</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שם משפחה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">המינימום ההכרחי ליצירת כרטיס</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מספר טלפון</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לזיהוי ולתקשורת עם המטופל
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">שדות מומלצים</h4>
        <p className="help-rich-text">
          שם פרטי, מייל, כתובת, תאריך לידה, פרטים נוספים (איש קשר לחירום, הורים),
          רקע רפואי (אבחנות, תרופות, אלרגיות) וניהול (שעות מועדפות, תעריף, סטטוס).
          הטופס מחולק ללשוניות כדי שיהיה נוח ומסודר.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            ככל שתמלא/י יותר מידע ברקע הקליני, כך סיכומי ה-AI לאחר טיפולים
            יהיו מדויקים יותר. אחרי השמירה, תועבר/י אוטומטית לכרטיס המטופל.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  calendar: {
    title: "יומן ופגישות",
    tooltipLabel: "היומן",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          היומן הוא המקום שבו מנהלים את כל הפגישות והתורים.
          בין אם אתם מתכננים ליום הבא או לחודש הקרוב — הכל כאן.
        </p>

        <h4 className="help-rich-heading">תצוגות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">יום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כל הפגישות של יום מסוים, שעה אחר שעה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שבוע</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              סקירה שבועית מלאה — מושלם לתכנון
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">חודש</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מבט-על על כל החודש
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">יצירת פגישה</h4>
        <p className="help-rich-text">
          לחיצה על משבצת ריקה ביומן פותחת טופס פגישה חדשה עם התאריך והשעה
          כבר מלאים. אפשר גם ללחוץ על כפתור &quot;+&quot; ליצירה ידנית.
          פגישות קיימות ניתנות לגרירה בין שעות ולעריכה בלחיצה.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            מהגדרות היומן (כפתור גלגל השיניים) אפשר לחסום ימים ספציפיים,
            להגדיר שעות עבודה שונות לכל יום, ולסנכרן עם Google Calendar.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  finance: {
    title: "פיננסים ותשלומים",
    tooltipLabel: "עמוד הפיננסים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          עמוד הפיננסים נותן לך תמונה מלאה על המצב הכספי של הקליניקה —
          הכנסות, חובות ותשלומים, הכל במקום אחד.
        </p>

        <h4 className="help-rich-heading">מה תמצא/י כאן</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">סיכום חודשי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כמה הכנסת החודש, והשוואה לחודשים קודמים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">חובות פתוחים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מטופלים שעדיין לא שילמו על טיפולים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">היסטוריית תשלומים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              רשימת כל התשלומים שהתקבלו
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אפשר ליצור הכנסה חדשה (כפתור &quot;+&quot;), לקשר תשלומים לפגישות
          ולהפיק חשבוניות.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם מורנינג (Green Invoice) מחובר, המערכת תציע להפיק חשבונית
            אוטומטית כשמטופל משלם — חוסך כמה דקות טובות בכל תשלום.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  finance_reports: {
    title: "דוחות חודשיים",
    tooltipLabel: "דוחות חודשיים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          עמוד הדוחות החודשיים מציג תמונה מעמיקה על הפעילות הפיננסית
          שלך בכל חודש — גרפים, מגמות וניתוח מפורט.
        </p>

        <h4 className="help-rich-heading">מה תמצא/י כאן</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">כרטיסי סיכום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הכנסה בפועל, מספר תשלומים, ממוצע לתשלום, אחוז גביה והפרש מהחודש הקודם
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מגמת הכנסות יומית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              גרף קו שמראה את ההכנסה המצטברת לאורך ימי החודש מול הצפי — ככה תוכל/י לראות אם את/ה בקצב טוב
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">הכנסות לפי מטופלים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              חלוקת ההכנסה לפי מטופלים — מי מביא הכי הרבה הכנסה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">אמצעי תשלום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              איך המטופלים שלך משלמים — מזומן, אשראי, העברה וכו&apos;
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">השוואת חודשים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              גרף שמשווה את ההכנסה בפועל מול הצפי ב-6 החודשים האחרונים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">התפלגות סטטוסים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כמה שולם, כמה ממתין, כמה בוטל — &quot;בריאות פיננסית&quot; של החודש
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">טבלה מפורטת</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כל התשלומים בפירוט, עם אפשרות מיון, סינון וייצוא ל-CSV
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">הסבר על המדדים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">אחוז גביה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כמה מתוך סך ההכנסה הצפויה של החודש כבר נגבה בפועל. 100% = הכל נגבה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">הפרש מחודש קודם</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              אחוז השינוי בהכנסה בפועל לעומת החודש שעבר
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            השתמש/י בכפתור &quot;ייצוא CSV&quot; כדי להוריד את הנתונים לאקסל —
            מעולה לצורכי הנהלת חשבונות ומעקב.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  toolbox: {
    title: "ארגז הכלים",
    tooltipLabel: "ארגז הכלים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          ארגז הכלים הוא המרכז לכל מה שעוזר לך לנהל את הקליניקה
          מעבר לפגישות עצמן — משימות, טפסים, שאלונים ועוד.
        </p>

        <h4 className="help-rich-heading">הכלים שלך</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">משימות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              רשימת ToDo אישית עם תאריכי יעד ועדיפויות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">פתקים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פתקים חכמים שצצים בזמן הנכון
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מתעניינים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              ניהול פניות של לקוחות פוטנציאליים חדשים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">טפסים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              טפסים דיגיטליים שנשלחים למטופל בקישור לחתימה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שאלונים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שאלוני הערכה ומעקב שהמטופלים ממלאים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תבניות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מסמכים מוכנים מראש לתיעוד טיפולים מהיר
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">התיקייה שלי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מאגר קבצים משותף לכל המטפלים בקליניקה
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שימו לב ל-badge (מספר) על כל לשונית — הוא מראה כמה פריטים
            ממתינים. טפסים ושאלונים נשלחים למטופל בקישור ייחודי,
            והוא יכול למלא מהטלפון בלי להתקין שום דבר.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  clinic_files: {
    title: "התיקייה שלי",
    tooltipLabel: "התיקייה שלי",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          מאגר קבצים משותף לכל הקליניקה — המקום לשמור חומרים
          שכל הצוות צריך גישה אליהם.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">העלאה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              PDF, תמונות, Word, Excel ועוד (גם בגרירה)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">ארגון</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              סידור הקבצים בתיקיות צבעוניות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">צפייה והדפסה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תצוגה מקדימה של תמונות ו-PDF, עם אפשרות הדפסה ישירה
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">שיתוף והרשאות</h4>
        <p className="help-rich-text">
          הקבצים משותפים לכל המטפלים. כל מטפל יכול לראות, להוריד ולהדפיס.
          רק מי שהעלה קובץ (או מנהל הקליניקה) יכול לערוך או למחוק אותו.
          הקבצים נספרים כחלק ממכסת האחסון הכוללת (1000MB),
          וגודל מקסימלי לקובץ הוא 15MB.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            מושלם לשמירת חומרי הדרכה, נהלים, תבניות טפסים
            וכל מסמך שכל הצוות צריך גישה אליו.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  forms: {
    title: "טפסים ותבניות",
    tooltipLabel: "טפסים ותבניות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          העמוד המרכזי לניהול כל הטפסים הדיגיטליים, השאלונים
          והתבניות שלך. הכל מסודר בלשוניות.
        </p>

        <h4 className="help-rich-heading">טפסים</h4>
        <p className="help-rich-text">
          יצירת טפסים דיגיטליים (הסכמה מדעת, קבלה, ויתור סודיות),
          שליחה למטופל בקישור לחתימה דיגיטלית, ומעקב —
          מי חתם ומי עדיין לא.
        </p>

        <h4 className="help-rich-heading">שאלונים</h4>
        <p className="help-rich-text">
          יצירת שאלוני הערכה מותאמים אישית, שליחה למטופלים
          ומעקב תשובות. מושלם למעקב התקדמות לאורך זמן.
        </p>

        <h4 className="help-rich-heading">תבניות</h4>
        <p className="help-rich-text">
          מסמכים מוכנים מראש לתיעוד טיפולים — בוחרים תבנית, ממלאים את הפרטים,
          וחוסכים זמן יקר. אפשר ליצור תבניות מותאמות לכל סוג טיפול.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור תבנית לכל סוג טיפול קבוע — זה חוסך המון זמן בתיעוד.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  treatment_doc: {
    title: "תיעוד טיפול",
    tooltipLabel: "תיעוד טיפול",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מתועד מה שקרה בטיפול — הסיכום, הממצאים ותוכנית ההמשך.
          זה המקום שבו הכל מתגבש למסמך מקצועי ומסודר.
        </p>

        <h4 className="help-rich-heading">מה יש בטופס</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">בחירת תבנית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תבנית מוכנה שמתאימה לסוג הטיפול
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרטי הטיפול</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              סוג טיפול, שיטה ונושאים שעלו
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תוכן חופשי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תיאור מפורט של מה שקרה במפגש
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">קבצים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              צירוף תמונות, מסמכים וציורים
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אחרי שמירה, התיעוד ננעל ולא ניתן לעריכה (לצורכי רגולציה),
          אבל תמיד אפשר להוסיף הערות.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            יש כפתור AI שמייצר סיכום אוטומטי על בסיס מה שכתבת.
            אם הפעלת &quot;מפגש חי&quot; לפני — אפשר לייבא את ההערות
            ישירות לתוך התיעוד.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  live_session: {
    title: "מפגש חי",
    tooltipLabel: "מפגש חי",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          מפגש חי (Live Session) הוא כלי מיוחד שעוזר לתעד טיפול
          בזמן אמת — רושמים נקודות קצרות תוך כדי עבודה עם המטופל,
          ומקבלים סיכום מקצועי בסוף.
        </p>

        <h4 className="help-rich-heading">איך זה עובד</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שעון וטיימר</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הזמן הנוכחי וכמה זמן נשאר לפגישה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">הערות מהירות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מילה, משפט, רעיון — תוך כדי המפגש
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סיכום AI</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              בסיום — סיכום מקצועי מתוך ההערות שרשמת
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">למה כדאי להשתמש</h4>
        <p className="help-rich-text">
          לא צריך לזכור הכל אחרי המפגש. רושמים נקודות קצרות,
          והסיכום נשמר ישירות בכרטיס המטופל. לא חייבים לכתוב
          משפטים שלמים — גם מילות מפתח מספיקות.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            בסיום המפגש, תועבר/י ישירות ליצירת תיעוד טיפול
            עם כל ההערות מיובאות ומוכנות.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  settings: {
    title: "הגדרות מטפל",
    tooltipLabel: "ההגדרות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מגדירים את הפרופיל האישי ואת ההעדפות שלך במערכת.
          זה המקום להתאים את הכל בדיוק לצורך שלך.
        </p>

        <h4 className="help-rich-heading">פרטים אישיים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרופיל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שם, טלפון, מייל ותמונת פרופיל
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">התמחות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כישורים ותחומי מומחיות — מוצגים בטפסים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תעריף</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תעריף שעתי ברירת מחדל לחישוב תשלומים
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">ניהול</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">התראות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              בחירה אילו התראות לקבל ובאיזו תדירות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תגים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              סטטוסי מתעניינים וסוגי מטופלים מותאמים אישית
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            עדכון תעריף כאן ישפיע על פגישות חדשות בלבד. יש גם
            &quot;הגדרות קליניקה&quot; בתפריט הצדדי — שם מנהל הקליניקה
            מגדיר דברים שמשפיעים על כולם.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  support_center: {
    title: "מרכז התמיכה",
    tooltipLabel: "מרכז התמיכה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          נתקעת? לא בטוח/ה איך לעשות משהו? רוצה לדווח על בעיה?
          מרכז התמיכה הוא המקום — ואנחנו תמיד כאן.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פנייה חדשה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תאר/י את הבעיה או השאלה, ונחזור אליך
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מעקב פניות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לראות סטטוס ותגובות על פניות קיימות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שיחה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              ממש כמו צ&apos;אט — להמשיך לכתוב הודעות על כל פנייה
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          הפנייה מגיעה ישירות אלינו ואנחנו עושים הכל כדי להגיב מהר.
          אפשר גם לשאול שאלות על איך להשתמש בפיצ&apos;ר מסוים —
          לא רק לדווח על בעיות.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            ככל שתתאר/י יותר בפירוט (מה עשית, מה ציפית שיקרה, מה קרה
            בפועל) — כך נוכל לעזור מהר יותר.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  // ============================================
  // MODAL-LEVEL HELP
  // ============================================

  appointment_modal: {
    title: "יצירת / עריכת פגישה",
    tooltipLabel: "יצירת פגישה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מגדירים את כל הפרטים של הפגישה — מטופל, מועד,
          סוג טיפול ואפשרויות נוספות.
        </p>

        <h4 className="help-rich-heading">פרטי מטופל ומועד</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">מטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              בוחרים מהרשימה, או יוצרים חדש ישירות מכאן
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תאריך ושעה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              אם נכנסתם מהיומן, הפרטים כבר מלאים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סדרת מפגשים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              אפשר להגדיר סדרה שבועית, דו-שבועית או חודשית
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">פרטים נוספים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">סוג טיפול וחדר</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              בחירה מרשימה או הקלדה חופשית
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">הערות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מידע פנימי שרואה רק את/ה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">SMS למטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שליחת תזכורת אוטומטית
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם הפגישה רטרואקטיבית (בתאריך שכבר עבר), המערכת
            תשאל לגבי סטטוס התשלום — ככה הכל מסודר מההתחלה.
            סדרת מפגשים מאפשרת עריכה או מחיקה של כל מפגש בנפרד.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  calendar_settings: {
    title: "הגדרות יומן",
    tooltipLabel: "הגדרות היומן",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מגדירים את שעות העבודה והזמינות שלך ביומן —
          פעם אחת, ואז רק מעדכנים לפי הצורך.
        </p>

        <h4 className="help-rich-heading">מה אפשר להגדיר</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שעות עבודה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שעות התחלה וסיום לכל יום בשבוע
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">ימי מנוחה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              ימים שלא עובדים בהם — פשוט לא מסמנים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">חסימת ימים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              חופשה, מילואים, יום חופש ספציפי
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">משך ברירת מחדל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              למשל 50 דקות — ישמש כברירת מחדל לפגישות חדשות
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שעות העבודה שמוגדרות כאן משפיעות על המשבצות הזמינות ביומן,
            ועל מה שמטופלים רואים אם יש להם קישור לקביעת תורים.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  income_modal: {
    title: "יצירת הכנסה",
    tooltipLabel: "יצירת הכנסה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מתעדים תשלום שהתקבל ממטופל — פשוט, מהיר ומסודר.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">מטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">בוחרים את המטופל שמשלם</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סכום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">כמה שולם</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">אמצעי תשלום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מזומן, אשראי, העברה או צ&apos;ק
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תאריך</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">מתי בוצע התשלום</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">קישור לפגישה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לקשר את התשלום לפגישה ספציפית
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אם יש חוב פתוח למטופל, המערכת תציע לקשר את התשלום אליו אוטומטית.
          אם הסכום ששולם קטן מהחוב — תתאפשר אפשרות של פיצול תשלום.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם מורנינג מחובר, תוכל/י להפיק חשבונית או קבלה ישירות
            אחרי תיעוד התשלום — בלחיצה אחת.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  invoice_modal: {
    title: "הפקת חשבונית",
    tooltipLabel: "הפקת חשבונית",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מפיקים חשבונית דרך מורנינג (Green Invoice) —
          ישירות מתוך המערכת, בלי לעבור לשירות אחר.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרטי הלקוח</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שם, ת.ז./ח.פ., כתובת (נמשכים אוטומטית מהכרטיס)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרטי החשבונית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              תיאור השירות, סכום ומע&quot;מ
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">אמצעי תשלום</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מזומן, אשראי, העברה או צ&apos;ק
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אחרי ההפקה, החשבונית נשמרת במורנינג ומקושרת לפגישה או לתשלום
          במערכת. אפשר להפיק חשבונית מס/קבלה (ברירת מחדל לעוסק מורשה), קבלה
          (עוסק פטור), חשבונית מס ללא קבלה או חשבונית זיכוי. ברירת המחדל
          נקבעת בהגדרות קליניקה ← אינטגרציות ← הגדרות הפקה.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם מילאת את פרטי המטופל בכרטיס שלו (ת.ז., כתובת),
            הם ימולאו אוטומטית בחשבונית — חוסך המון זמן.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  // ============================================
  // TOOLBOX TAB-LEVEL HELP
  // ============================================

  tasks_tab: {
    title: "משימות",
    tooltipLabel: "לשונית המשימות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          רשימת ה-ToDo האישית שלך כמטפל. כשיש מטופלים רבים
          והרבה דברים לזכור — כאן אפשר לרשום הכל ולעקוב.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">יצירת משימה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כותרת, תיאור, תאריך יעד ועדיפות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">קישור למטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לקשר משימה למטופל ספציפי
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">עדכון סטטוס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פתוח, בתהליך, ממתין, הסתיים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סינון</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לפי סטטוס — לראות רק מה רלוונטי
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            משימות עם תאריך יעד שעבר מסומנות באדום. משימות פתוחות
            מופיעות גם בדף הבית — ככה לא תפספס/י דברים חשובים.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  reminders_tab: {
    title: "פתקים",
    tooltipLabel: "לשונית הפתקים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          פתקים הם הדרך שלך להגיד למערכת: &quot;תזכיר/י לי על X בעוד Y
          זמן&quot;. בניגוד למשימה שהיא &quot;משהו שצריך לעשות&quot;, פתק הוא
          &quot;משהו שצריך לזכור&quot;.
        </p>

        <h4 className="help-rich-heading">דוגמאות לשימוש</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-desc">
              לעקוב אחרי המצב של שרה בעוד שבוע
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-desc">
              לבדוק אם דני מילא את השאלון
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-desc">
              לחדש ביטוח מקצועי בעוד חודש
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אפשר לקשר כל פתק למטופל ספציפי ולהגדיר קטגוריה.
          פתקים פעילים מופיעים גם בדף הבית.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            השתמש/י בפתקים למעקב טיפולי — למשל, פתק לבדוק
            התקדמות חודש אחרי סיום טיפול.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  leads_tab: {
    title: "מתעניינים",
    tooltipLabel: "לשונית המתעניינים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          מתעניין הוא לקוח פוטנציאלי — מישהו שהתעניין בשירות שלך
          אבל עדיין לא הפך למטופל. כאן עוקבים אחרי כל פנייה
          עד שהופכת לטיפול (או לא).
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">יצירת מתעניין</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שם, טלפון ומקור ההגעה (פייסבוק, המלצה, גוגל)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">עדכון סטטוס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מעוניין, בטיפול ראשוני, הפך למטופל או דחוי
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">יומן התפתחות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מתי דיברתם, מה סוכם, מה השלב הבא
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">המרה למטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              להפוך מתעניין למטופל בלחיצה כשהוא מתקדם
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            סטטוסי המתעניינים מותאמים אישית — אפשר לערוך ולהוסיף
            אותם בהגדרות המטפל. מתעניינים פעילים מופיעים גם בדף הבית.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  forms_tab: {
    title: "טפסים",
    tooltipLabel: "לשונית הטפסים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן יוצרים ומנהלים טפסים דיגיטליים שנשלחים למטופלים
          לחתימה — הכל קורה בקישור, ישירות מהטלפון.
        </p>

        <h4 className="help-rich-heading">איך זה עובד</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 1</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              יוצרים טופס (הסכמה מדעת, קבלה, ויתור סודיות)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 2</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שולחים למטופל בקישור ייחודי (SMS או מייל)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 3</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              המטופל פותח, קורא וחותם דיגיטלית מהטלפון
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 4</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הטופס החתום מופיע בתיק המטופל
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אפשר ליצור טפסים עם עורך טקסט עשיר, לדרוש חתימה דיגיטלית,
          ולשלוח תזכורת למי שלא חתם. לשונית &quot;ממתינים&quot; מראה
          מי עדיין לא חתם.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור את הטפסים הנפוצים פעם אחת, ואז פשוט לשלוח
            אותם לכל מטופל חדש. המטופל לא צריך להתקין שום דבר.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  questionnaires_tab: {
    title: "שאלונים",
    tooltipLabel: "לשונית השאלונים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          שאלונים מאפשרים לאסוף מידע מובנה מהמטופלים בצורה
          נוחה ומקצועית — הם ממלאים מהטלפון, ואת/ה מקבל/ת
          את התשובות ישירות בכרטיס.
        </p>

        <h4 className="help-rich-heading">סוגי שאלות</h4>
        <p className="help-rich-text">
          טקסט חופשי, בחירה מרובה וסקאלה. אפשר ליצור שאלונים
          מותאמים אישית לכל צורך.
        </p>

        <h4 className="help-rich-heading">שימושים נפוצים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-desc">
              שאלוני BDI / PHQ-9 / GAD-7
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-desc">שאלון שביעות רצון</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-desc">מעקב שבועי או חודשי</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-desc">שאלון קבלה ראשוני</span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שלח/י את אותו שאלון מדי פעם כדי לעקוב אחרי התקדמות
            לאורך זמן — תוכל/י להשוות תשובות בין תקופות.
            שאלונים שלא מולאו עדיין מופיעים בלשונית &quot;ממתינים למענה&quot;.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  templates_tab: {
    title: "תבניות",
    tooltipLabel: "לשונית התבניות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          תבניות הן מסמכים מוכנים מראש שחוסכים זמן יקר
          כשמתעדים טיפולים — בוחרים תבנית, ממלאים את הפרטים, וסיימנו.
        </p>

        <h4 className="help-rich-heading">איך זה עובד</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 1</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              יוצרים תבנית עם שדות מותאמים (מטרת טיפול, ממצאים, המלצות)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 2</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כשמתעדים טיפול — בוחרים תבנית
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שלב 3</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              המסמך נפתח עם המבנה מוכן — רק ממלאים תוכן
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">למה זה שווה</h4>
        <p className="help-rich-text">
          חוסך זמן (לא מתחילים מאפס), מבטיח אחידות (כל התיעודים באותו
          פורמט) ומאפשר שדות מותאמים לכל סוג טיפול.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור תבנית לכל סוג טיפול קבוע — למשל:
            &quot;CBT - מפגש רגיל&quot;, &quot;אבחון ראשוני&quot;,
            &quot;סיכום ביניים&quot;. ה-AI יכול לייצר סיכום מועשר על בסיס התבנית.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  // ============================================
  // MODAL-LEVEL HELP (create/edit dialogs)
  // ============================================

  task_modal: {
    title: "יצירת / עריכת משימה",
    tooltipLabel: "משימה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          משימות הן רשימת ה-ToDo החכמה שלך — כדי שלא ישכח
          שום דבר, גם כשיש הרבה מטופלים ועניינים בראש.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">כותרת</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מה צריך לעשות (&quot;להתקשר לדני&quot;, &quot;להזמין ציוד&quot;)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תיאור</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פירוט נוסף אם צריך
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תאריך יעד</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">מתי צריך לסיים</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">עדיפות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">גבוהה, בינונית או נמוכה</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">קישור למטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              אופציונלי — לקשר למטופל ספציפי
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            משימות עם תאריך יעד שעבר מופיעות עם סימון אדום.
            משימות פתוחות מופיעות גם בדף הבית — ככה לא מפספסים.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  reminder_modal: {
    title: "יצירת / עריכת פתק",
    tooltipLabel: "פתק",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          פתקים הם הדרך להגיד למערכת &quot;תזכיר/י לי&quot; —
          בשונה ממשימה (משהו שצריך לעשות), פתק הוא משהו שצריך לזכור.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">כותרת</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מה לזכור (&quot;לעקוב אחרי שרה&quot;, &quot;לשלוח טופס הערכה&quot;)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תיאור</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מידע נוסף שיעזור לך כשתראה/י את הפתק
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תאריך</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">מתי להציג את הפתק</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">קטגוריה ומטופל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              סיווג וקישור למטופל ספציפי
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            השתמש/י בשניהם — משימות ופתקים — כדי לא לפספס שום דבר.
            פתקים פעילים מופיעים גם בדף הבית בסעיף התזכורות.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  lead_modal: {
    title: "יצירת / עריכת מתעניין",
    tooltipLabel: "מתעניין",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          מתעניין הוא לקוח פוטנציאלי שהתעניין בשירות שלך.
          כאן ממלאים את הפרטים ועוקבים אחרי ההתקדמות
          עד שהוא הופך למטופל.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שם</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">שם הפונה</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרטי קשר</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">טלפון ו/או מייל</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מקור</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              איך הגיע? (פייסבוק, המלצה, גוגל)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סטטוס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מעוניין, בטיפול ראשוני, הפך למטופל, או דחוי
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אפשר לתעד יומן התפתחות (מתי דיברתם, מה סוכם)
          ולהפוך את המתעניין למטופל בלחיצה כשהוא מתקדם.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            סטטוסי המתעניינים מותאמים אישית — אפשר להוסיף ולערוך
            בהגדרות המטפל. מתעניינים פעילים מופיעים גם בדף הבית.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  form_modal: {
    title: "יצירת / עריכת טופס",
    tooltipLabel: "טופס",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          טפסים דיגיטליים הם דרך מצוינת לאסוף מידע ממטופלים
          בצורה מסודרת ומקצועית — בלי נייר, בלי סריקות.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שם הטופס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              למשל &quot;הסכמה מדעת&quot;, &quot;טופס קבלה&quot;
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תוכן</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הטקסט שיופיע למטופל (עורך טקסט עשיר)
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">חתימה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              האם לדרוש חתימה דיגיטלית
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אחרי היצירה, שולחים בקישור ייחודי. המטופל ממלא מהטלפון,
          חותם, והטופס מופיע בתיק שלו.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור את הטפסים הנפוצים פעם אחת ולשלוח לכל מטופל
            חדש. בלשונית &quot;ממתינים לחתימה&quot; תראו מי עדיין לא חתם.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  template_modal: {
    title: "יצירת / עריכת תבנית",
    tooltipLabel: "תבנית",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          תבניות הן מסמכים מוכנים מראש שמקלים על תיעוד טיפולים —
          במקום להתחיל מאפס בכל פעם, בוחרים תבנית וממלאים.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שם התבנית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              למשל &quot;סיכום טיפול CBT&quot;, &quot;אבחון ראשוני&quot;
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תוכן</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הטקסט הבסיסי שיופיע בכל שימוש בתבנית
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          כשתתעד/י טיפול חדש, תוכל/י לבחור תבנית ולקבל מסמך מוכן
          שרק צריך למלא את הפרטים הספציפיים של אותה פגישה.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור תבנית לכל סוג טיפול קבוע — &quot;CBT - מפגש
            רגיל&quot;, &quot;אבחון ראשוני&quot;, &quot;סיכום ביניים&quot;.
            ה-AI יכול לייצר סיכום מועשר על בסיס התבנית.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  questionnaire_modal: {
    title: "יצירת / עריכת שאלון",
    tooltipLabel: "שאלון",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          שאלונים מאפשרים לאסוף מידע מובנה מהמטופלים —
          הם ממלאים מהטלפון, ואת/ה מקבל/ת את התשובות
          ישירות בכרטיס.
        </p>

        <h4 className="help-rich-heading">מה ממלאים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">שם השאלון</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              למשל &quot;שאלון BDI&quot;, &quot;שביעות רצון&quot;, &quot;מעקב שבועי&quot;
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שאלות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מסוגים שונים: טקסט חופשי, בחירה מרובה, סקאלה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">דרישת חתימה דיגיטלית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מתג בתחתית. כשפעיל, המטופל חותם (ביד או בהקלדת שם) בסוף
              השאלון ולא יכול לשלוח בלי חתימה. החתימה נשמרת עם התשובות
              ומופיעה ב-PDF. חל על שליחות חדשות בלבד.
            </span>
          </div>
        </div>

        <p className="help-rich-text">
          אחרי היצירה, שולחים בקישור. המטופלים ממלאים מהטלפון
          והתשובות מופיעות ישירות בכרטיס המטופל.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שאלונים מעולים למעקב התקדמות — שלח/י את אותו שאלון כל
            חודש כדי לראות שינויים לאורך זמן. שאלונים שלא מולאו
            מופיעים בלשונית &quot;ממתינים למענה&quot;.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  patient_files: {
    title: "תיק מטופל (קבצים)",
    tooltipLabel: "תיק המטופל",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          תיק המטופל הוא המקום שבו שמורים כל הקבצים הקשורים
          למטופל הזה — הכל מרוכז ומסודר במקום אחד.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">העלאה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מסמכים, תמונות, אישורים ומכתבים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">ארגון בתיקיות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              יצירת תיקיות חדשות וגרירת קבצים ביניהן
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תיאור</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              עריכת תיאור לכל קובץ — לדעת מה בפנים בלי לפתוח
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מחיקה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מחיקת קבצים ותיקיות שלא צריך
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            כדאי ליצור מבנה תיקיות קבוע (למשל: &quot;אישורים&quot;, &quot;צילומים&quot;,
            &quot;מכתבי הפניה&quot;). קבצים שמצורפים לתיעוד טיפול מופיעים גם
            כאן — הכל מרוכז.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  morning_setup: {
    title: "חיבור מורנינג (Green Invoice)",
    tooltipLabel: "חיבור מורנינג",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          מורנינג (Green Invoice) הוא שירות חשבוניות דיגיטליות שמאפשר
          להפיק חשבוניות מס, קבלות ומסמכים כספיים ישירות מתוך CliniKit.
        </p>

        <h4 className="help-rich-heading">למה כדאי לחבר</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">חשבוניות בלחיצה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הפקה ישירות מהפגישה או מעמוד הפיננסים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מילוי אוטומטי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פרטי המטופל נמשכים ישירות מהכרטיס
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">שמירה ותיעוד</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כל המסמכים נשמרים — אפשר לצפות ולהוריד
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">תואם לחוק</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              חשבוניות מס תקינות לפי דרישות רשות המסים
            </span>
          </div>
        </div>

        <h4 className="help-rich-heading">תהליך החיבור</h4>
        <p className="help-rich-text">
          נכנסים למורנינג ויוצרים API Token, מדביקים אותו כאן באשף,
          בוחרים את העסק מהרשימה ומאשרים — והחיבור מוכן.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            אם אין לך עדיין חשבון במורנינג — אפשר להירשם בחינם
            באתר שלהם ואחר כך לחזור לחבר. החיבור מאובטח ואנחנו
            לא שומרים סיסמאות, רק Token גישה מוגבל.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  team: {
    title: "צוות הקליניקה",
    tooltipLabel: "צוות הקליניקה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן מנהלים את אנשי הצוות: הוספה, תפקיד, הרשאות אישיות, מטופלים
          משויכים והשבתה. כל שינוי נכנס לתוקף מיידית, ללא התחברות מחדש.
        </p>
        <h4 className="help-rich-heading">תפקידים</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">בעל/ת הקליניקה</span>
            <span className="help-rich-item-sep">—</span>
            <span>גישה מלאה. היחיד/ה שיכול/ה למנות ולהסיר מנהלים.</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מנהל/ת קליניקה</span>
            <span className="help-rich-item-sep">—</span>
            <span>זהה לבעלים בכל חלקי המערכת, למעט מינוי מנהלים.</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מטפל/ת</span>
            <span className="help-rich-item-sep">—</span>
            <span>מטופלים, יומן ודוחות אישיים. הרשאות נוספות לפי בחירה.</span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מזכירות</span>
            <span className="help-rich-item-sep">—</span>
            <span>ללא יומן ומטופלים אישיים — תיאום תורים וקבלה עבור הצוות. נספר/ת כמקום בתוכנית.</span>
          </div>
        </div>
        <h4 className="help-rich-heading">הרשאות</h4>
        <p>
          לכל תפקיד ברירת מחדל. הרשאה אישית גוברת על ברירת המחדל של הקליניקה.
          הרשאות המסומנות באזהרה חושפות מידע רפואי — ודאו שהן ניתנות לפי נהלי
          הקליניקה. כל צפייה בדוח של מטפל/ת אחר/ת נרשמת ביומן.
        </p>
      </div>
    ),
  },
  clinic_settings: {
    title: "הגדרות קליניקה",
    tooltipLabel: "הגדרות הקליניקה",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          הגדרות הקליניקה הן הגדרות שמשפיעות על כל המטפלים —
          בניגוד להגדרות מטפל שהן אישיות. רק מנהל הקליניקה
          יכול לגשת לכאן.
        </p>

        <h4 className="help-rich-heading">מה יש כאן</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פרטי קליניקה</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              שם, כתובת, לוגו ומספר עסק
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">ניהול מטפלים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הוספת מטפלים, הגדרת הרשאות וצפייה בפעילות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">ניהול חדרים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הגדרת חדרי טיפול לתיאום פגישות
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">התאמה אישית</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              צבעים, סוגי טיפול ושדות מותאמים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">אינטגרציות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              חיבור מורנינג, Google Calendar ועוד
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">יומן פעילות</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מעקב אחרי פעולות שנעשו במערכת
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שינויים כאן (כמו הוספת חדר) משפיעים מיידית על כל המטפלים.
            אם אתה מטפל רגיל ולא רואה את העמוד — זה תקין,
            רק מנהלים רואים אותו.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  // ============================================
  // ADMIN (CLINIC MANAGER) PAGES
  // ============================================

  admin_snapshot: {
    title: "תמונת מצב",
    tooltipLabel: "תמונת מצב",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          תמונת המצב של הקליניקה — העמוד שנותן לך מבט-על
          על מה שקורה אצל כל המטפלים, הכנסות ומטופלים.
        </p>

        <h4 className="help-rich-heading">סטטיסטיקות למעלה</h4>
        <p className="help-rich-text">
          מספרים שמסכמים את השבוע: כמה פגישות בכל הקליניקה,
          מטופלים פעילים, מטפלים פעילים וסטטוס גבייה (חובות
          פתוחים ותשלומים שבוצעו).
        </p>

        <h4 className="help-rich-heading">הכרטיסיות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">פעילות מטפלים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              כמה פגישות לכל מטפל ושיעור ניצול
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">פילוח מטופלים</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              התפלגות לפי סטטוס, סוג ומטפל
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">מצב כספי</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              הכנסות, חובות ומגמות
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            העמוד מתעדכן אוטומטית. אם מספר כלשהו מפתיע — לחצ/י
            על הכרטיסייה הרלוונטית כדי להבין לעומק. רק מנהל
            הקליניקה רואה את העמוד הזה.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  admin_patients: {
    title: "ניהול מטופלים",
    tooltipLabel: "ניהול מטופלים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן אפשר לראות את כל המטופלים של כל המטפלים בקליניקה
          במקום אחד — חיפוש, סינון וצפייה כוללת.
        </p>

        <h4 className="help-rich-heading">מה אפשר לעשות</h4>
        <div className="help-rich-items">
          <div className="help-rich-item">
            <span className="help-rich-item-title">חיפוש</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לפי שם או טלפון — בכל המטופלים בקליניקה
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סינון לפי מטפל</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              לראות רק את המטופלים של מטפל מסוים
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">סינון לפי סטטוס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              פעיל, בהמתנה, סיים טיפול
            </span>
          </div>
          <div className="help-rich-item">
            <span className="help-rich-item-title">צפייה בכרטיס</span>
            <span className="help-rich-item-sep">—</span>
            <span className="help-rich-item-desc">
              מצב צפייה בלבד — לשמירה על פרטיות ואחריות
            </span>
          </div>
        </div>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            השתמש/י בסינון לפי מטפל כדי לבדוק עומס ולוודא חלוקה שווה.
            מטופל ששייך למטפל אחד לא מופיע אצל מטפל אחר —
            כל מטפל רואה רק את שלו.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  admin_calendar: {
    title: "יומנים ותורים",
    tooltipLabel: "יומנים ותורים",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן אפשר לראות את היומנים של כל המטפלים בקליניקה —
          ואם הוגדרו חדרים, גם את תפוסת החדרים. מושלם לתכנון
          יום העבודה.
        </p>

        <h4 className="help-rich-heading">תצוגת מטפלים</h4>
        <p className="help-rich-text">
          כל מטפל מוצג בכרטיסייה עם מספר הפגישות שלו היום
          ושעות העבודה המוגדרות. לחיצה על מטפל פותחת את
          היומן המלא שלו.
        </p>

        <h4 className="help-rich-heading">תצוגת חדרים</h4>
        <p className="help-rich-text">
          אם הקליניקה מוגדרת עם חדרי טיפול — כל חדר מוצג בנפרד,
          עם מי שתופס אותו בכל שעה. עוזר לזהות חפיפות
          ובעיות תיאום.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            במבט אחד אפשר לראות מי פנוי, איזה חדר זמין ומתי יש
            &quot;פקקים&quot;. הפגישות מוצגות בצפייה בלבד — כדי לערוך,
            צריך להיכנס ליומן של המטפל.
          </span>
        </div>
      </div>
    ),
    image: null,
  },

  admin_tasks: {
    title: "ניהול משימות ותזכורות",
    tooltipLabel: "ניהול משימות ותזכורות",
    content: (
      <div className="help-rich">
        <p className="help-rich-intro">
          כאן אפשר לראות את כל המשימות והתזכורות של כל המטפלים
          בקליניקה — מעקב מרכזי שמוודא שדברים לא נופלים בין
          הכיסאות.
        </p>

        <h4 className="help-rich-heading">לשונית משימות</h4>
        <p className="help-rich-text">
          כל המשימות של כל המטפלים, עם אפשרות סינון לפי מטפל
          ולפי סטטוס (פתוח, בתהליך, ממתין, הסתיים). רואים מי
          יצר כל משימה ומתי.
        </p>

        <h4 className="help-rich-heading">לשונית תזכורות</h4>
        <p className="help-rich-text">
          כל התזכורות של כל המטפלים במקום אחד — סינון לפי מטפל
          ולפי סטטוס, עם צפייה במועד התזכורת ולמי היא מקושרת.
        </p>

        <h4 className="help-rich-heading">למה זה שימושי</h4>
        <p className="help-rich-text">
          לוודא שמטפלים מטפלים במשימות בזמן, לזהות עומס
          (מטפל עם הרבה משימות פתוחות אולי צריך עזרה),
          ומעקב כללי על תפקוד הקליניקה.
        </p>

        <div className="help-rich-tip">
          <span className="help-rich-tip-label">טיפ</span>
          <span>
            שימו לב למשימות עם תאריך יעד שעבר (מסומנות באדום) —
            זה סימן שמשהו צריך טיפול. כל מטפל רואה רק את המשימות
            שלו בעמוד הרגיל.
          </span>
        </div>
      </div>
    ),
    image: null,
  },
};
